import type { AdminPartnerView, AdminPayout } from '@trendator/shared';
import { useState } from 'react';
import { Link } from '../components/Layout';
import { Button, Card, CardTitle, Empty, ErrorBox, Field, FilterButton, Input, Label, Loading, Modal, Status, Table, useToast, type Tone } from '../components/ui';
import { api, errorText } from '../lib/api';
import { dateTime, num, rub, shortId, userName, when } from '../lib/format';
import { useLoad } from '../lib/hooks';
import { setQuery } from '../lib/router';

const FILTERS = [
  ['pending', 'Ждут выплаты'],
  ['paid', 'Выплачены'],
  ['rejected', 'Отклонены'],
  ['all', 'Все'],
] as const;

export const PAYOUT_STATUS: Record<AdminPayout['status'], { label: string; tone: Tone }> = {
  pending: { label: 'Ждёт', tone: 'warn' },
  paid: { label: 'Выплачено', tone: 'ok' },
  rejected: { label: 'Отклонено', tone: 'idle' },
};
export const METHOD = { sbp: 'СБП', usdt: 'USDT TRC-20' } as const;

const ENTRY: Record<string, string> = {
  accrual: 'начисление',
  reversal: 'сторно (возврат платежа)',
  transfer: 'на баланс',
  payout: 'вывод',
  payout_cancel: 'отказ в выводе',
  adjustment: 'корректировка',
};

export function payoutDetails(p: AdminPayout): string {
  return p.method === 'sbp' ? `${p.details.phone ?? ''} · ${p.details.bank ?? ''}` : (p.details.address ?? '');
}

export function Partners({ query }: { query: URLSearchParams }) {
  const toast = useToast();
  const filter = (FILTERS.find(([id]) => id === query.get('status'))?.[0] ?? 'pending') as (typeof FILTERS)[number][0];
  const d = useLoad(() => api.get<AdminPartnerView>(`/partner${filter === 'all' ? '' : `?status=${filter}`}`), [filter]);
  const [decide, setDecide] = useState<{ payout: AdminPayout; kind: 'paid' | 'reject' } | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!decide) return;
    setBusy(true);
    try {
      await api.post(`/partner/payouts/${decide.payout.id}/${decide.kind}`, { note: note.trim() || undefined });
      toast(decide.kind === 'paid' ? 'Отмечено как выплачено, юзеру написали' : 'Отклонено, деньги вернулись на счёт партнёра');
      setDecide(null);
      setNote('');
      await d.reload();
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  };

  if (d.error && !d.data) return <ErrorBox text={d.error} onRetry={() => void d.reload()} />;
  const t = d.data?.totals;

  return (
    <>
      <h1 className="m-0 text-2xl font-extrabold tracking-tight lg:text-[30px]">Партнёрка</h1>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Начислено всего" value={t ? rub(t.accrued) : '…'} />
        <Stat label="В холде" value={t ? rub(t.held) : '…'} note="ещё могут сгореть при возврате платежа" />
        <Stat label="На счетах партнёров" value={t ? rub(t.onAccounts) : '…'} />
        <Stat label="Ждут выплаты" value={t ? rub(t.pendingAmount) : '…'} note={t ? `${num(t.pendingPayouts)} заявок · выплачено всего ${rub(t.paidOut)}` : undefined} tone={t?.pendingPayouts ? 'warn' : undefined} />
      </div>

      <Card className="flex flex-col gap-3 px-2 pt-3 pb-1">
        <div className="flex flex-wrap items-center gap-2 px-2">
          <span className="mr-2 text-base font-bold">Заявки на вывод</span>
          {FILTERS.map(([id, label]) => (
            <FilterButton key={id} on={filter === id} onClick={() => setQuery({ status: id === 'pending' ? null : id })}>
              {label}
            </FilterButton>
          ))}
        </div>
        {!d.data ? <Loading /> : null}
        {d.data && d.data.payouts.length === 0 ? <Empty>{filter === 'pending' ? 'Новых заявок нет' : 'Пусто'}</Empty> : null}
        {d.data && d.data.payouts.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>Заявка</th>
                <th>Партнёр</th>
                <th>Куда</th>
                <th>Статус</th>
                <th className="text-right!">Сумма</th>
                <th className="text-right!">Создана</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.data.payouts.map((p) => (
                <tr key={p.id}>
                  <td className="font-mono text-xs text-muted">#{shortId(p.id)}</td>
                  <td>
                    <Link to={`/users/${p.user.id}`} className="font-semibold text-white">
                      {userName(p.user)}
                    </Link>
                  </td>
                  <td>
                    <span className="flex flex-col">
                      <span className="text-soft">{METHOD[p.method]}</span>
                      <span className="font-mono text-xs break-all text-text2 select-all">{payoutDetails(p)}</span>
                    </span>
                  </td>
                  <td>
                    <Status tone={PAYOUT_STATUS[p.status].tone}>{PAYOUT_STATUS[p.status].label}</Status>
                    {p.note ? <span className="block text-xs text-muted">{p.note}</span> : null}
                  </td>
                  <td className="text-right font-semibold">{rub(p.amount)}</td>
                  <td className="text-right text-muted" title={dateTime(p.createdAt)}>
                    {when(p.createdAt)}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {p.status === 'pending' ? (
                      <span className="inline-flex gap-1.5">
                        <Button size="sm" variant="accent" onClick={() => setDecide({ payout: p, kind: 'paid' })}>
                          Выплачено
                        </Button>
                        <Button size="sm" onClick={() => setDecide({ payout: p, kind: 'reject' })}>
                          Отклонить
                        </Button>
                      </span>
                    ) : (
                      <span className="text-xs text-muted">{p.decidedBy}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : null}
      </Card>

      <Card className="flex flex-col gap-3 px-2 pt-3 pb-1">
        <CardTitle right={<Label>последние 100</Label>} className="px-2">
          Движения по счетам
        </CardTitle>
        {d.data && d.data.entries.length === 0 ? <Empty>Начислений пока нет</Empty> : null}
        {d.data && d.data.entries.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <th>Партнёр</th>
                <th>Операция</th>
                <th>Комментарий</th>
                <th className="text-right!">Сумма</th>
                <th className="text-right!">Когда</th>
              </tr>
            </thead>
            <tbody>
              {d.data.entries.map((e) => {
                const held = e.type === 'accrual' && e.availableAt && Date.parse(e.availableAt) > Date.now();
                return (
                  <tr key={e.id}>
                    <td>
                      <Link to={`/users/${e.partner.id}`} className="font-semibold text-white">
                        {userName(e.partner)}
                      </Link>
                    </td>
                    <td className="text-soft">
                      {ENTRY[e.type] ?? e.type}
                      {e.percent ? ` · ${e.percent}%` : ''}
                      {held ? <span className="ml-1.5 rounded bg-warn/14 px-1.5 text-[11px] font-bold text-warn">ХОЛД до {new Date(e.availableAt!).toLocaleDateString('ru-RU')}</span> : null}
                    </td>
                    <td className="text-muted">
                      {e.comment}
                      {e.referralUserId ? (
                        <>
                          {' · '}
                          <Link to={`/users/${e.referralUserId}`}>друг #{e.referralUserId}</Link>
                        </>
                      ) : null}
                    </td>
                    <td className={e.amount > 0 ? 'text-right font-semibold text-ok' : 'text-right font-semibold'}>
                      {e.amount > 0 ? '+' : '−'}
                      {rub(Math.abs(e.amount))}
                    </td>
                    <td className="text-right text-muted">{when(e.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : null}
      </Card>

      <Modal
        open={decide !== null}
        onClose={() => setDecide(null)}
        title={decide?.kind === 'paid' ? `Выплатили ${decide ? rub(decide.payout.amount) : ''}?` : 'Отклонить заявку?'}
      >
        {decide ? (
          <>
            <span className="text-sm text-soft">
              {decide.kind === 'paid'
                ? `Переведи ${rub(decide.payout.amount)} на ${METHOD[decide.payout.method]}: ${payoutDetails(decide.payout)}. Потом отметь здесь — юзеру придёт сообщение.`
                : 'Деньги вернутся на партнёрский счёт, юзер увидит причину в боте.'}
            </span>
            <Field label={decide.kind === 'paid' ? 'Номер перевода / хэш (необязательно)' : 'Причина (обязательно)'}>
              <Input value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder={decide.kind === 'paid' ? 'например, СБП 1234' : 'например, неверный номер'} />
            </Field>
            <Button variant={decide.kind === 'paid' ? 'accent' : 'danger'} busy={busy} disabled={decide.kind === 'reject' && note.trim().length < 3} onClick={() => void submit()}>
              {decide.kind === 'paid' ? 'Отметить выплаченным' : 'Отклонить'}
            </Button>
          </>
        ) : null}
      </Modal>
    </>
  );
}

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'warn' }) {
  return (
    <Card className="flex flex-col gap-1">
      <Label>{label}</Label>
      <b className={tone === 'warn' ? 'text-2xl text-warn' : 'text-2xl'}>{value}</b>
      {note ? <span className="text-xs text-muted">{note}</span> : null}
    </Card>
  );
}
